import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';
import DropZone from 'components/DropZone';
import ProductFormFields from './ProductFormFields';

const validationSchema = Yup.object({
  nameBG: Yup.string().required('Задължително поле'),
  nameRU: Yup.string().required('Задължително поле'),
  nameEN: Yup.string().required('Задължително поле'),
  code: Yup.string().required('Задължително поле'),
  status: Yup.string().required('Задължително поле'),
  category: Yup.number().required('Задължително поле'),
  subCategory: Yup.number().required('Задължително поле'),
  productTypeAttribute: Yup.number().required('Задължително поле'),
  materialAttribute: Yup.number().required('Задължително поле'),
  descriptionBG: Yup.string().required('Задължително поле'),
  descriptionRU: Yup.string().required('Задължително поле'),
  descriptionEN: Yup.string().required('Задължително поле'),
  summaryBG: Yup.string().required('Задължително поле'),
  summaryRU: Yup.string().required('Задължително поле'),
  summaryEN: Yup.string().required('Задължително поле')
});

export default function CloneProduct() {
  const params = useParams();
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [imagesData, setImagesData] = useState([]);
  const [newProductId, setNewProductId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [cloneFeedback, setCloneFeedback] = useState(null);
  const [imageFeedback, setImageFeedback] = useState(null);

  useEffect(() => {
    const loadPage = async () => {
      setIsLoading(true);
      try {
        const [
          productResponse,
          categoryResponse,
          subcategoryResponse,
          typeResponse,
          materialResponse
        ] = await Promise.all([
          axios.get('/singleProduct/', { params }),
          axios.get('/category'),
          axios.get('/subcategory'),
          axios.get('/productType'),
          axios.get('/material')
        ]);
        const sourceProduct = productResponse.data[0];
        if (!sourceProduct) throw new Error('Product not found');
        sourceProduct.status = 'draft';
        setProduct(sourceProduct);
        setCategories(categoryResponse.data);
        setSubcategories(subcategoryResponse.data);
        setProductTypes(typeResponse.data);
        setMaterials(materialResponse.data);
      } catch (error) {
        setLoadError('Продуктът-шаблон не можа да бъде зареден.');
      } finally {
        setIsLoading(false);
      }
    };
    loadPage();
  }, [params.id]);

  const initialValues = {
    copyProductCode: params.id,
    status: product?.status || 'draft',
    nameBG: product?.name_bg || '',
    nameRU: product?.name_ru || '',
    nameEN: product?.name_en || '',
    code: '',
    position: 1000000000,
    category: product?.categoryId || '',
    subCategory: product?.subCategoryId || '',
    productTypeAttribute: product?.productTypeAttributeId || '',
    materialAttribute: product?.materialAttributeId || '',
    summaryBG: product?.summary_bg || '',
    summaryRU: product?.summary_ru || '',
    summaryEN: product?.summary_en || '',
    descriptionBG: product?.description_bg || '',
    descriptionRU: product?.description_ru || '',
    descriptionEN: product?.description_en || ''
  };

  const handleSubmit = async (values) => {
    setCloneFeedback({
      severity: 'info',
      message: 'Създаване на продукта и копиране на вариациите…'
    });
    setNewProductId(null);
    try {
      const response = await axios.post('/cloneProduct', { values });
      const targetId = response.data.targetId;
      setNewProductId(targetId);

      await axios.post('/cloneProductSizes', {
        copyProductCode: values.copyProductCode,
        targetId
      });
      await axios.post('/cloneProductPrices', {
        copyProductCode: values.copyProductCode,
        targetId
      });

      setCloneFeedback({
        severity: 'success',
        message: `Новият продукт #${targetId} и неговите цени бяха създадени успешно.`
      });
    } catch (error) {
      setCloneFeedback({
        severity: 'error',
        message:
          'Продуктът не можа да бъде клониран изцяло. Моля проверете данните и опитайте отново.'
      });
    }
  };

  const handleAddImages = async () => {
    if (!newProductId || !imagesData.length) return;
    setImageFeedback({ severity: 'info', message: 'Качване на снимките…' });
    try {
      for (const image of imagesData) {
        await axios.post('/createProductImages', {
          imagesData: [image],
          targetId: newProductId
        });
      }
      setImagesData([]);
      setImageFeedback({ severity: 'success', message: 'Снимките бяха добавени успешно.' });
    } catch (error) {
      setImageFeedback({ severity: 'error', message: 'Някои снимки не можаха да бъдат качени.' });
    }
  };

  const headerActions = (
    <Stack direction="row" gap={1}>
      <Button component={Link} to="/products" startIcon={<ArrowBackRoundedIcon />}>
        Назад
      </Button>
      {product && (
        <Button
          variant="outlined"
          component="a"
          href={`https://gotobed.eu/produkt/${product.slug_bg}/${product.id}`}
          target="_blank"
          rel="noreferrer"
          endIcon={<OpenInNewRoundedIcon />}
        >
          Виж шаблона
        </Button>
      )}
    </Stack>
  );

  return (
    <VendorDashboardLayout>
      <DashboardPageHeader
        title="Клониране на продукт"
        subtitle={
          product ? `Шаблон: ${product.name_bg} · ${product.code} · #${product.id}` : 'Зареждане…'
        }
        icon={ContentCopyRoundedIcon}
        actions={headerActions}
      />

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      )}
      {loadError && <Alert severity="error">{loadError}</Alert>}

      {product && (
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent
                sx={{ p: { xs: 2.5, md: 4 }, '&:last-child': { pb: { xs: 2.5, md: 4 } } }}
              >
                <Alert severity="info" sx={{ mb: 3 }}>
                  Размерите и цените ще бъдат копирани от продукта-шаблон. Можете да промените
                  текста, категорията, материала и типа преди създаване.
                </Alert>
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Form>
                      <ProductFormFields
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        categories={categories}
                        subcategories={subcategories}
                        productTypes={productTypes}
                        materials={materials}
                        positionField="position"
                        positionLabel="Позиция"
                      />
                      {cloneFeedback && (
                        <Alert severity={cloneFeedback.severity} sx={{ mt: 3 }}>
                          {cloneFeedback.message}
                          {newProductId && (
                            <Button
                              component={Link}
                              to={`/productDetails/${newProductId}`}
                              size="small"
                              sx={{ ml: 1 }}
                            >
                              Отвори продукта
                            </Button>
                          )}
                        </Alert>
                      )}
                      {Object.keys(errors).length > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          Проверете задължителните полета във всички езикови табове.
                        </Alert>
                      )}
                      <Stack direction="row" justifyContent="flex-end" mt={3}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<ContentCopyRoundedIcon />}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Създаване…' : 'Създай копие'}
                        </Button>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={3} sx={{ position: { lg: 'sticky' }, top: { lg: 96 } }}>
              <Card>
                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                  <Stack direction="row" alignItems="center" gap={1} mb={2}>
                    <ImageRoundedIcon color="primary" />
                    <Typography variant="h6">Снимка на шаблона</Typography>
                  </Stack>
                  {product.images?.[0]?.url ? (
                    <Box
                      component="img"
                      src={product.images[0].url}
                      alt={product.name_bg}
                      sx={{ borderRadius: 3, height: 220, objectFit: 'cover', width: '100%' }}
                    />
                  ) : (
                    <Box sx={{ bgcolor: 'grey.100', borderRadius: 3, p: 5, textAlign: 'center' }}>
                      <ImageRoundedIcon sx={{ color: 'grey.300', fontSize: 46 }} />
                    </Box>
                  )}
                  <Stack direction="row" justifyContent="space-between" mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      Статус на копието
                    </Typography>
                    <Chip
                      label="Чернова"
                      color="error"
                      size="small"
                      sx={{ bgcolor: '#991b1b', color: '#ffffff' }}
                    />
                  </Stack>
                </CardContent>
              </Card>

              <Card>
                <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                  <Typography variant="h6" mb={0.5}>
                    Снимки за новия продукт
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Първо създайте копието, след това качете избраните изображения.
                  </Typography>
                  <DropZone imagesData={imagesData} setImagesData={setImagesData} />
                  {imageFeedback && (
                    <Alert severity={imageFeedback.severity} sx={{ mt: 2 }}>
                      {imageFeedback.message}
                    </Alert>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleAddImages}
                    disabled={!newProductId || !imagesData.length}
                  >
                    Качи снимките
                  </Button>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      )}
    </VendorDashboardLayout>
  );
}
